import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, FolderOpen, Clock, Loader2, ArrowLeft, FolderKanban, PauseCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { getContrastText, getContrastMuted, ColorPicker } from "../components/ColorPicker";
import { PeoplePicker } from "../components/PeoplePicker";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  "not started": "status-not-started",
  "open": "status-open",
  "waiting": "status-waiting",
  "closed": "status-closed",
};

// Matches SILENCE_THRESHOLD_DAYS in backend/feed.py.
const SILENCE_THRESHOLD_DAYS = 14;

const DEFAULT_COLOR = "#3B82F6";

// Every status goes through a translation.
function statusLabel(value, t, s) {
  if (value === "waiting") return s("proj.statusWaiting");
  if (value === "not started") return t("status.notStarted");
  if (value === "open") return t("status.open");
  if (value === "closed") return t("status.closed");
  return value;
}

// The four pieces below are declared here rather than inside the page.
// Defined in the body of a component they would be a different function on
// every render, which React reads as a different kind of component: the whole
// card grid was being torn down and rebuilt on each keystroke in the search
// box instead of being updated.

// Marks a field that can be left alone. Only the title is required, and
// without saying so the dialog reads as four things to fill in before a
// project can exist.
function Optional({ s }) {
  return (
    <span className="ml-1.5 text-xs font-normal text-slate-400">
      ({s("common.optional")})
    </span>
  );
}

function SectionHeading({ children }) {
  return (
    <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
      {children}
    </h2>
  );
}

function ProjectCard({ project, dimmed = false, t, s }) {
  const cardBg = project.color || DEFAULT_COLOR;
  const textColor = getContrastText(cardBg);
  const mutedColor = getContrastMuted(cardBg);
  const days = project.silence_days;
  const alarming =
    typeof days === "number" && days >= SILENCE_THRESHOLD_DAYS && project.status !== "closed";
  const people = project.people || [];

  return (
    <Link
      to={`/projects/${project.id}`}
      data-testid={`project-card-${project.id}`}
      className={
        "block rounded-lg p-5 transition-all duration-200 group hover:shadow-lg hover:brightness-95" +
        (dimmed ? " opacity-75" : "")
      }
      style={{ backgroundColor: cardBg }}
    >
      {/* Wraps: on a narrow card a long status label and the silence badge
          together are wider than the row, and without wrap the badge would
          be pushed off the card's right edge. */}
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <span className={`tag ${STATUS_STYLES[project.status] || STATUS_STYLES["not started"]}`}>
          {statusLabel(project.status, t, s)}
        </span>

        {/* The clock in this corner used to be decorative. It now carries the
            single most useful number on the card: how long since a person
            last said anything here. */}
        {typeof days === "number" && (
          <span
            data-testid={`project-silence-${project.id}`}
            className={
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 " +
              (alarming ? "bg-white/95 text-amber-700 shadow-sm" : "")
            }
            style={alarming ? undefined : { color: mutedColor }}
          >
            <Clock className="w-3.5 h-3.5" />
            {days === 0 ? s("proj.today") : s("proj.silentDays", { days })}
          </span>
        )}
      </div>

      {/* break-words: a project title can be one long unbroken string, a
          part number or a URL, and without it the card stretches past the
          screen instead of wrapping. */}
      <h3
        className="text-lg font-semibold mb-1.5 leading-snug break-words"
        style={{ color: textColor }}
      >
        {project.title}
      </h3>

      {project.description && (
        <p className="text-sm line-clamp-2 mb-3 break-words" style={{ color: mutedColor }}>
          {project.description}
        </p>
      )}

      {project.waiting_for && (
        <div
          data-testid={`project-waiting-${project.id}`}
          className="flex items-start gap-1.5 mb-3 text-xs min-w-0"
          style={{ color: mutedColor }}
        >
          <PauseCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span className="line-clamp-1 min-w-0">
            {s("proj.waitingLabel")}: {project.waiting_for}
          </span>
        </div>
      )}

      {people.length > 0 && (
        <div className="flex -space-x-1.5" data-testid={`project-people-${project.id}`}>
          {people.slice(0, 4).map((p) => (
            <span
              key={p.id}
              title={p.display_name}
              className="w-6 h-6 rounded-full ring-2 ring-white text-white text-[10px] font-semibold flex items-center justify-center shrink-0"
              style={{ backgroundColor: p.color || "#3730A3" }}
            >
              {(p.display_name || "?").charAt(0).toUpperCase()}
            </span>
          ))}
          {people.length > 4 && (
            <span className="w-6 h-6 rounded-full ring-2 ring-white bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center shrink-0">
              +{people.length - 4}
            </span>
          )}
        </div>
      )}
    </Link>
  );
}

function Grid({ items, dimmed = false, t, s }) {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((project) => (
        <ProjectCard key={project.id} project={project} dimmed={dimmed} t={t} s={s} />
      ))}
    </div>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const s = localStrings(language);

  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Whether the closed list is showing. null until somebody says so: the page
  // decides on its own below, and stops deciding as soon as it is told.
  const [showClosed, setShowClosed] = useState(null);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("not started");
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [people, setPeople] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(data);
    } catch (error) {
      toast.error(t("toast.failedLoad"));
    } finally {
      setLoading(false);
    }

    // The directory returns names and colours only and is open to every user,
    // unlike /users which is admin-only. Fetched separately and allowed to
    // fail, so a problem here leaves the picker empty rather than taking the
    // whole page down.
    try {
      const usersRes = await axios.get(`${API}/users/directory`, { withCredentials: true });
      setAllUsers(usersRes.data || []);
    } catch {
      setAllUsers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const togglePerson = (id) => {
    const key = String(id);
    setPeople((prev) =>
      prev.map(String).includes(key)
        ? prev.filter((p) => String(p) !== key)
        : [...prev, key]
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/projects`,
        { title, description, status, color, people },
        { withCredentials: true }
      );
      setProjects([data, ...projects]);
      setDialogOpen(false);
      setTitle("");
      setDescription("");
      setStatus("not started");
      setColor(DEFAULT_COLOR);
      setPeople([]);
      toast.success(t("toast.projectCreated"));
    } catch (error) {
      toast.error(s("err.projectSave"));
    } finally {
      setCreating(false);
    }
  };

  const filteredProjects = projects.filter((project) => {
    const matchesStatus = filter === "all" || project.status === filter;
    const matchesSearch =
      searchQuery === "" ||
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // The server returns these quietest-first and filtering preserves that, so
  // the alarm still works inside each group below.
  const activeProjects = filteredProjects.filter((p) => p.status !== "closed");
  const closedProjects = filteredProjects.filter((p) => p.status === "closed");

  // Closed projects only accumulate, and every one of them sat on this page
  // permanently, in a run nobody scrolls to on purpose. They are behind their
  // heading now, which opens, and carries the count so the number is known
  // without opening anything.
  //
  // Until somebody clicks, the page decides: shut while there is something
  // else to look at, open when there is not. Filtering the status to closed,
  // or searching for a name that only survives among them, leaves nothing
  // above it - and a search that answers with a shut bar is a search that
  // looks like it found nothing.
  const closedOpen = showClosed === null ? activeProjects.length === 0 : showClosed;

  const myId = String(user?._id ?? "");
  const isMine = (p) => (p.people || []).some((person) => String(person.id) === myId);
  // Projects you are on come first, the rest below, grouped rather than
  // filtered. A toggle that hides other people's projects would recreate the
  // exact problem Core exists to fix: not knowing what exists.
  const mine = activeProjects.filter(isMine);
  const others = activeProjects.filter((p) => !isMine(p));

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div>
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("projects.backToHome")}
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center shrink-0">
                <FolderKanban className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("projects.title")}</h1>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-project-btn"
                  className="bg-indigo-600 hover:bg-indigo-500 text-white btn-scale w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("projects.newProject")}
                </Button>
              </DialogTrigger>
              {/* Scrolls: with the people picker the dialog is taller than a
                  phone in landscape, and a form that cannot reach its own
                  submit button cannot be submitted. */}
              <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">{t("projects.create.title")}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">{t("projects.create.name")}</Label>
                    <Input
                      id="title"
                      data-testid="project-title-input"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={t("projects.create.namePlaceholder")}
                      required
                      className="border-slate-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">
                      {t("projects.create.description")}
                      <Optional s={s} />
                    </Label>
                    <Textarea
                      id="description"
                      data-testid="project-description-input"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("projects.create.descriptionPlaceholder")}
                      rows={3}
                      className="border-slate-200 resize-none"
                    />
                  </div>

                  {/* The people a project concerns, chosen while creating it.
                      This existed only inside a project, under Last activity,
                      so nothing here suggested a project could have people at
                      all - and projects were being created without any. Still
                      editable there afterwards; this is the same list, offered
                      at the moment somebody is already thinking about who the
                      project is for. */}
                  <div className="space-y-2">
                    <Label>
                      {s("proj.people")}
                      <Optional s={s} />
                    </Label>
                    <PeoplePicker
                      allUsers={allUsers}
                      selectedIds={people}
                      onToggle={togglePerson}
                      s={s}
                    />
                  </div>

                  {/* The colour belongs to the project and is picked here,
                      replacing the admin-managed category list. */}
                  <ColorPicker label={s("proj.color")} value={color} onChange={setColor} />

                  <div className="space-y-2">
                    <Label htmlFor="status">{t("projects.create.status")}</Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="project-status-select" className="border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not started">{t("status.notStarted")}</SelectItem>
                        <SelectItem value="open">{t("status.open")}</SelectItem>
                        <SelectItem value="waiting">{s("proj.statusWaiting")}</SelectItem>
                        <SelectItem value="closed">{t("status.closed")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="border-slate-200"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      data-testid="submit-project-btn"
                      disabled={creating}
                      className="bg-indigo-600 hover:bg-indigo-500 text-white btn-scale"
                    >
                      {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("projects.create.submit")}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Filters. Both take the full width on a phone: capped at 320px and
              160px they sat in the middle of the screen with dead space beside
              them, which reads as a layout that failed rather than as a
              choice. */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <Input
              data-testid="search-projects-input"
              placeholder={t("projects.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:max-w-xs border-slate-200"
            />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="filter-status-select" className="w-full sm:w-40 border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("projects.allStatus")}</SelectItem>
                <SelectItem value="not started">{t("status.notStarted")}</SelectItem>
                <SelectItem value="open">{t("status.open")}</SelectItem>
                <SelectItem value="waiting">{s("proj.statusWaiting")}</SelectItem>
                <SelectItem value="closed">{t("status.closed")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : activeProjects.length === 0 && closedProjects.length === 0 ? (
            <div className="text-center py-20">
              <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t("projects.noProjects")}</p>
            </div>
          ) : (
            <>
              {mine.length > 0 && (
                <div className="mb-10" data-testid="my-projects-section">
                  <SectionHeading>{s("proj.mine")}</SectionHeading>
                  <Grid items={mine} t={t} s={s} />
                </div>
              )}

              {others.length > 0 && (
                <div data-testid="other-projects-section">
                  {mine.length > 0 && <SectionHeading>{s("proj.others")}</SectionHeading>}
                  <Grid items={others} t={t} s={s} />
                </div>
              )}

              {activeProjects.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-400 text-sm">{t("projects.noActive")}</p>
                </div>
              )}

              {closedProjects.length > 0 && (
                <div className="mt-12" data-testid="closed-projects-section">
                  <button
                    type="button"
                    onClick={() => setShowClosed(!closedOpen)}
                    aria-expanded={closedOpen}
                    data-testid="closed-projects-toggle"
                    className="w-full flex items-center gap-2 pb-2 border-b border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors group"
                  >
                    <ChevronRight
                      className={
                        "w-4 h-4 shrink-0 transition-transform duration-200 " +
                        (closedOpen ? "rotate-90" : "")
                      }
                    />
                    <span className="text-sm font-semibold uppercase tracking-wide">
                      {t("projects.closedSection")}
                    </span>
                    <span className="ml-auto text-sm font-medium tabular-nums text-slate-400 group-hover:text-slate-600 transition-colors">
                      {closedProjects.length}
                    </span>
                  </button>

                  {closedOpen && (
                    <div className="mt-6">
                      <Grid items={closedProjects} dimmed t={t} s={s} />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
